import { useEffect, useState } from 'react';
import { Plus, Trash2, RefreshCw } from 'lucide-react';
import { apiClient } from '@/api/client';
import { formatPrice, type SwipaPackage, type BillingOrder } from '@/api/billing';

interface Summary {
  byStatus: { status: string; count: number; amountKopecks: number; swipaAmount: number }[];
  revenueKopecks: number;
  swipaSold: number;
  paidOrders: number;
}

interface PackageDraft {
  id?: string;
  name: string;
  swipaAmount: number | string;
  bonusSwipa: number | string;
  priceRub: number | string; // редактируем в рублях, шлём в копейках
  active: boolean;
  sort: number | string;
}

const emptyDraft = (): PackageDraft => ({
  name: '', swipaAmount: 100, bonusSwipa: 0, priceRub: 99, active: true, sort: 0,
});

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Ожидает', PAID: 'Оплачен', CREDITED: 'Зачислен',
  FAILED: 'Ошибка', CANCELED: 'Отменён', REFUNDED: 'Возврат',
};
const STATUSES = ['ALL', 'CREDITED', 'PENDING', 'FAILED', 'CANCELED'] as const;

export default function AdminBilling() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [orders, setOrders] = useState<(BillingOrder & { user?: { username: string; displayName: string } })[]>([]);
  const [packages, setPackages] = useState<SwipaPackage[]>([]);
  const [statusFilter, setStatusFilter] = useState<(typeof STATUSES)[number]>('ALL');
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<PackageDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, o, p] = await Promise.all([
        apiClient.get<Summary>('/api/admin/billing/summary'),
        apiClient.get<{ data: typeof orders }>('/api/admin/billing/orders', {
          params: statusFilter === 'ALL' ? {} : { status: statusFilter },
        }),
        apiClient.get<SwipaPackage[]>('/api/admin/billing/packages'),
      ]);
      setSummary(s.data);
      setOrders(o.data.data);
      setPackages(p.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter]);

  const startEdit = (pkg: SwipaPackage) => {
    setDraft({
      id: pkg.id, name: pkg.name, swipaAmount: pkg.swipaAmount,
      bonusSwipa: pkg.bonusSwipa, priceRub: pkg.priceKopecks / 100,
      active: pkg.active, sort: pkg.sort,
    });
    setError(null);
  };

  const savePackage = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: draft.name.trim(),
        swipaAmount: Number(draft.swipaAmount) || 1,
        bonusSwipa: Number(draft.bonusSwipa) || 0,
        priceKopecks: Math.round((Number(draft.priceRub) || 0) * 100),
        active: draft.active,
        sort: Number(draft.sort) || 0,
      };
      if (draft.id) await apiClient.put(`/api/admin/billing/packages/${draft.id}`, body);
      else await apiClient.post('/api/admin/billing/packages', body);
      setDraft(null);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message ?? e.message ?? 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const removePackage = async (id: string) => {
    if (!confirm('Деактивировать пакет? Исторические заказы не затрагиваются.')) return;
    await apiClient.delete(`/api/admin/billing/packages/${id}`);
    await load();
  };

  return (
    <div className="p-4 max-w-5xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-surface-100">Биллинг — продажи SWIPA</h2>
        <button onClick={load} className="px-3 py-1.5 rounded bg-surface-800 hover:bg-surface-700 text-surface-200 text-sm flex items-center gap-1.5">
          <RefreshCw size={14} /> Обновить
        </button>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-surface-900 border border-surface-800 rounded-xl p-4">
            <p className="text-surface-500 text-xs">Выручка (зачислено)</p>
            <p className="text-2xl font-bold text-green-400 mt-1">{formatPrice(summary.revenueKopecks)}</p>
          </div>
          <div className="bg-surface-900 border border-surface-800 rounded-xl p-4">
            <p className="text-surface-500 text-xs">SWIPA продано</p>
            <p className="text-2xl font-bold text-yellow-400 mt-1">{summary.swipaSold.toLocaleString('ru-RU')}</p>
          </div>
          <div className="bg-surface-900 border border-surface-800 rounded-xl p-4">
            <p className="text-surface-500 text-xs">Оплаченных заказов</p>
            <p className="text-2xl font-bold text-surface-100 mt-1">{summary.paidOrders}</p>
          </div>
        </div>
      )}

      {/* Packages */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-surface-200">Пакеты пополнения</h3>
        <button onClick={() => { setDraft(emptyDraft()); setError(null); }}
          className="px-3 py-1.5 rounded bg-brand-600 hover:bg-brand-500 text-white text-sm flex items-center gap-1.5">
          <Plus size={14} /> Пакет
        </button>
      </div>

      {draft && (
        <div className="bg-surface-900 border border-surface-700 rounded-xl p-4 mb-4">
          {error && <p className="text-red-400 text-sm mb-2">{error}</p>}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <label className="text-xs text-surface-400">Название
              <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className="mt-1 w-full bg-surface-800 border border-surface-700 rounded px-2 py-1.5 text-sm text-surface-100" />
            </label>
            <label className="text-xs text-surface-400">SWIPA
              <input type="number" value={draft.swipaAmount} onChange={(e) => setDraft({ ...draft, swipaAmount: e.target.value })}
                className="mt-1 w-full bg-surface-800 border border-surface-700 rounded px-2 py-1.5 text-sm text-surface-100" />
            </label>
            <label className="text-xs text-surface-400">Бонус SWIPA
              <input type="number" value={draft.bonusSwipa} onChange={(e) => setDraft({ ...draft, bonusSwipa: e.target.value })}
                className="mt-1 w-full bg-surface-800 border border-surface-700 rounded px-2 py-1.5 text-sm text-surface-100" />
            </label>
            <label className="text-xs text-surface-400">Цена, ₽
              <input type="number" value={draft.priceRub} onChange={(e) => setDraft({ ...draft, priceRub: e.target.value })}
                className="mt-1 w-full bg-surface-800 border border-surface-700 rounded px-2 py-1.5 text-sm text-surface-100" />
            </label>
            <label className="text-xs text-surface-400">Сортировка
              <input type="number" value={draft.sort} onChange={(e) => setDraft({ ...draft, sort: e.target.value })}
                className="mt-1 w-full bg-surface-800 border border-surface-700 rounded px-2 py-1.5 text-sm text-surface-100" />
            </label>
            <label className="text-xs text-surface-400 flex items-center gap-2 mt-5">
              <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
              Активен
            </label>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={savePackage} disabled={saving}
              className="px-3 py-1.5 rounded bg-brand-600 hover:bg-brand-500 text-white text-sm disabled:opacity-40">
              {saving ? 'Сохраняем…' : 'Сохранить'}
            </button>
            <button onClick={() => setDraft(null)} className="px-3 py-1.5 rounded bg-surface-800 text-surface-300 text-sm">Отмена</button>
          </div>
        </div>
      )}

      <div className="space-y-1.5 mb-6">
        {packages.map((p) => (
          <div key={p.id} className="flex items-center justify-between bg-surface-900 border border-surface-800 rounded-lg px-3 py-2 text-sm">
            <div className="flex items-center gap-3 min-w-0">
              <span className={p.active ? 'text-surface-100' : 'text-surface-500 line-through'}>{p.name}</span>
              <span className="text-yellow-400 font-medium">{(p.swipaAmount + p.bonusSwipa).toLocaleString('ru-RU')} SWIPA</span>
              <span className="text-surface-400">{formatPrice(p.priceKopecks, p.currency)}</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => startEdit(p)} className="text-brand-400 hover:underline text-xs">Изменить</button>
              <button onClick={() => removePackage(p.id)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Orders */}
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-sm font-semibold text-surface-200">Заказы</h3>
        <div className="ml-auto flex gap-1">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-2 py-1 rounded text-xs ${statusFilter === s ? 'bg-brand-600 text-white' : 'bg-surface-800 text-surface-400'}`}>
              {s === 'ALL' ? 'Все' : STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><div className="animate-spin w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full" /></div>
      ) : !orders.length ? (
        <p className="text-surface-500 text-sm py-6 text-center">Заказов нет</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-surface-500 text-xs border-b border-surface-800">
                <th className="text-left py-2 font-medium">Пользователь</th>
                <th className="text-left py-2 font-medium">SWIPA</th>
                <th className="text-left py-2 font-medium">Сумма</th>
                <th className="text-left py-2 font-medium">Провайдер</th>
                <th className="text-left py-2 font-medium">Статус</th>
                <th className="text-left py-2 font-medium">Дата</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-surface-800/60">
                  <td className="py-2 text-surface-200">{o.user?.username ?? '—'}</td>
                  <td className="py-2 text-yellow-400">{o.swipaAmount.toLocaleString('ru-RU')}</td>
                  <td className="py-2 text-surface-300">{formatPrice(o.amountKopecks, o.currency)}</td>
                  <td className="py-2 text-surface-400">{o.provider}</td>
                  <td className="py-2 text-surface-300">{STATUS_LABEL[o.status] ?? o.status}</td>
                  <td className="py-2 text-surface-500 text-xs">{new Date(o.createdAt).toLocaleString('ru-RU')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
